import { useMutation, useQuery } from '@tanstack/react-query';

import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';
import { MasterData } from './master-data';
import { FileData, FilePayload } from './types';

export type SPFSearchOption = {
	searchByOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
	spfStatusOptions: MasterData[];
	customerStatusOptions: MasterData[];
	applicationStatusOptions: MasterData[];
	frmOptions: MasterData[];
	fbdhOptions: MasterData[];
};

export const getSPFSearchOption = () => {
	return fetcher<SPFSearchOption>({
		api: '/spf/get-search-options',
	});
};

export const useSPFSearchOptions = () => {
	return useQuery({
		queryKey: ['useSelectSPFSearchOptions'],
		queryFn: async () => getSPFSearchOption(),
	});
};

export type SearchSPFResult = {
	id?: number;
	napResolutionId: number;
	prospectId: string;
	napId: number;
	napNo: string;
	spfNo: string;
	customerId: string;
	customerName: string;
	bmppGroupName: string;
	plafonTypes: MasterData[];
	financingLimitTypes: MasterData[];
	spfStatus: MasterData;
	customerStatus: MasterData;
	applicationStatus: MasterData | null;
	requestStatus: MasterData;
	frmName: string;
	fbdhName: string;
	ccaName: string;
	allowInput: boolean;
	allowDownload: boolean;
	allowApprove: boolean;
	allowUpload: boolean;
	allowDeviation: boolean;
	allowEditResolution: boolean;
};

export type SearchSPFParam = FleetGetListParams &
	Partial<{
		plafonTypeId: number;
		financingLimitTypeId: number;
		spfStatusId: number;
		customerStatusId: number;
		applicationStatusId: number;
		frmId: number;
		fbdhId: number;
	}>;

const searchSPF = (body: SearchSPFParam) => {
	return fetcher<FleetPaginated<SearchSPFResult>>({
		api: '/spf/search',
		body,
	});
};

export const useSPFSearch = (param: SearchSPFParam) => {
	return useQuery({
		queryKey: ['searchSPF', param] as const,
		queryFn: () => searchSPF(param),
	});
};

export type SPFDetail = {
	customerId: string;
	customerName: string;
	prospectId: string;
	bmppGroupId: string;
	bmppGroupName: string;
	napId: number;
	napNo: string;
	napResolutionId: number | null;
	portfolioGuideline: string;
	ultimateOwner: string;
	applicationDate: string;
	resolutionId: number;
	rkpDate: string;
	resolutionFullApprovedDate: string;
	requestStatus: number;
	applicationStatus: MasterData;
	facilityNotifications: {
		id: number;
		resolutionPlafonId: number;
		wayOfPaymentId: number;
		facilityValidityPeriod: string;
		disbursementMechanism: string;
		financingObjectCondition: string;
		financingObjectEvaluation: string;
		plafonTypeId: number;
		requestedLimit: number;
		approvedLimit: number;
		financingLimitTypeId: number;
		financingTypes: MasterData[];
	}[];
	facilityNotificationDetails: Record<
		'approvalFacilityNotifications' | 'requestFacilityNotifications',
		{
			id: number;
			resolutionPlafonId: number;
			wayOfPaymentId: number;
			facilityValidityPeriod: string;
			disbursementMechanism: string;
			financingObjectCondition: string;
			financingObjectEvaluation: string;
			plafonTypeId: number;
			requestedLimit: number;
			approvedLimit: number;
			financingLimitTypeId: number;
			financingTypes: MasterData[];
			facilityTypes: MasterData[];
		}
	>[];
	facilities: {
		id?: number;
		assetType: MasterData;
		financingObjectCondition: MasterData;
		plafonType: MasterData;
		financingLimitType?: MasterData;
		tenor: MasterData;
		interestRateIndicationPercent: number;
		downPaymentPercent: number;
		securityDeposit: number;
		administrativeFee: number;
		provisionFee: number;
		serviceFee: number;
		fiduciaryFee: number;
		insurance: MasterData;
		insuranceDiscountPercentage?: number;
	}[];
	covenants: {
		id: number;
		covenant: string;
		category: MasterData;
		repeatFrequency: MasterData;
		repeatFrequencyCustom?: {
			id?: number;
			repeatValue: number;
			repeatFrequency: MasterData;
			onPattern: {
				dayOfMonth: number;
				month: MasterData;
			};
			onThePattern: {
				position: MasterData;
				dayOfWeek: MasterData;
				month: MasterData;
			};
		};
		monitoringType: MasterData;
		furtherAnalysis: MasterData;
		priority: MasterData;
		uploadDocumentDate: string;
		covenantDocument: FileData;
		dueDate: string;
		overdueDays: number;
		notes?: string;
		status: string;
	}[];
	frmNotes?: string;
	requestEditCovenant: boolean;
	notes: string;
	racDeviationDocument: {
		id: string;
		name: string;
		path: string;
		url: string;
		format: string;
	};
	approvalHistory: {
		id: number;
		name: string;
		date: string;
		process: string;
		status: string;
		notes: string;
	}[];
	napPlafonTypeId?: number;
	rejectCategory: MasterData;
	rejectReason?: string;
	resolutionFile?: FileData;
	financingTypeOptions: MasterData[];
	facilityTypeOptions: MasterData[];
	financingLimitTypeOptions: MasterData[];
	wayOfPaymentOptions: MasterData[];
	rejectCategoryOptions: MasterData[];
	assetTypeOptions: MasterData[];
	financingObjectConditionOptions: MasterData[];
	plafonTypeOptions: MasterData[];
	tenorOptions: MasterData[];
	insuranceOptions: MasterData[];
	approvalActionOptions: MasterData[];
	categoryOptions: MasterData[];
	priorityOptions: MasterData[];
	repeatFrequencyOptions: MasterData[];
	customRepeatFrequencyOptions: MasterData[];
	monitoringType: MasterData[];
	furtherAnalysisOptions: MasterData[];
	monthOptions: MasterData[];
	positionOptions: MasterData[];
	dayOfWeekOptions: MasterData[];
};

export type GetSPFDetailParam = {
	prospectId?: string;
	napResolutionId: number;
	spfId?: number;
};

const getSPFDetail = (body: GetSPFDetailParam) => {
	return fetcher<SPFDetail>({
		api: '/spf/get-detail',
		body,
	});
};

export const useGetSPFDetail = (param: GetSPFDetailParam) => {
	return useQuery({
		queryKey: ['getSPFDetail', param],
		queryFn: () => getSPFDetail(param),
	});
};

export type SPFFacilityPayload = {
	id: number;
	assetTypeId: number;
	financingObjectConditionId: number;
	plafonTypeId: number;
	financingLimitNature?: string;
	tenorId: number;
	interestRateIndicationPercent: number;
	downPaymentPercent: number;
	securityDeposit: number;
	administrativeFee: number;
	provisionFee: number;
	serviceFee: number;
	fiduciaryFee: number;
	insuranceId: number;
	insuranceDiscountPercentage?: number;
};

export type SPFFaclityNotificationPayload = {
	id?: number;
	resolutionPlafonId: number;
	wayOfPaymentId: number;
	facilityValidityPeriod: string;
	disbursementMechanism: string;
	financingObjectCondition: string;
	financingObjectEvaluation: string;
	plafonTypeId: number;
	requestedLimit: number;
	approvedLimit: number;
	financingLimitTypeId: number;
	financingTypeIds?: number[];
};

export type SPFCovenantPayload = {
	id?: number;
	covenant: string;
	categoryId: number;
	priorityId: number;
	repeatFrequencyId: number;
	monitoringTypeId: number;
	furtherAnalysisId: number;
	repeatFrequencyCustom: {
		id: number;
		repeatValue: number;
		repeatFrequencyId: number;
		onPattern: {
			dayOfMonth: number;
			monthId: number;
		};
		onThePattern: {
			positionId: number;
			dayOfWeekId: number;
			monthId: number;
		};
	};
	statusId?: string;
	dueDate: string;
	covenantDocument?: FilePayload;
	uploadDocumentDate?: string;
	notes?: string;
};

export type SubmitSPFPayload = {
	id?: number;
	prospectId?: string;
	napResolutionId?: number;
	facilityNotifications: SPFFaclityNotificationPayload[];
	facilities: SPFFacilityPayload[];
	covenants: SPFCovenantPayload[];
	notes: string;
	// userd for review SPF
	applicationDate?: string;
	rkpDate?: string;
	resolutionFullApprovedDate: string;
	action?: string;
	// used for submit SPF, deviation & edit resolution
	deviationDocument?: FilePayload;
	requestEditCovenant?: boolean;
};

export type SubmitSPFProspectRejectPayload = {
	prospectId: string;
	napResolutionId: number | null;
	rejectCategoryId: number;
	rejectReason: string;
	notes: string;
};

const submitSPF = (body: SubmitSPFPayload) => {
	return fetcher({
		api: '/spf/submit',
		body,
	});
};

const submitSPFProspectReject = (body: SubmitSPFProspectRejectPayload) => {
	return fetcher({
		api: '/spf/submit',
		body,
	});
};

export const useSubmitSPFMutation = () => {
	return useMutation({
		mutationFn: submitSPF,
	});
};

export const useSubmitSPFProspectRejectMutation = () => {
	return useMutation({
		mutationFn: submitSPFProspectReject,
	});
};

export type SubmitSPFApprovalPayload = {
	id: number;
	action: string;
	notes: string;
	// rejectCategoryId?: number;
};

export type SubmitSPFRejectPayload = {
	prospectId?: string;
	napResolutionId?: number | null;
	rejectCategoryId: number;
	rejectReason: string;
};

const submitSPFApproval = (body: SubmitSPFApprovalPayload) => {
	return fetcher<string>({
		api: '/spf/submit-approval',
		body,
	});
};

const submitSPFReject = (body: SubmitSPFRejectPayload) => {
	return fetcher<string>({
		api: '/spf/submit-reject',
		body,
	});
};

export const useSubmitSPFApprovalMutation = () => {
	return useMutation({
		mutationFn: submitSPFApproval,
	});
};

export const useSubmitSPFRejectMutation = () => {
	return useMutation({
		mutationFn: submitSPFReject,
	});
};

const submitSPFDeviation = (body: SubmitSPFPayload) => {
	return fetcher({
		api: '/spf/submit-deviation',
		body,
	});
};

export const useSubmitSPFDeviationMutation = () => {
	return useMutation({
		mutationFn: submitSPFDeviation,
	});
};

export type SubmitEditResolutionPayload = {
	id: number;
	notes: string;
};

const submitSPFEditResolution = (body: SubmitEditResolutionPayload) => {
	return fetcher({
		api: '/spf/submit-edit-resolution',
		body,
	});
};

export const useSubmitSPFEditResolutionMutation = () => {
	return useMutation({
		mutationFn: submitSPFEditResolution,
	});
};

const submitSPFReview = (body: SubmitSPFPayload) => {
	return fetcher({
		api: '/spf/review-spf',
		body,
	});
};

export const useSubmitSPFReviewMutation = () => {
	return useMutation({
		mutationFn: submitSPFReview,
	});
};

export type UploadSPFDocumentPayload = {
	spfId: number;
	document: {
		id: string;
		name: string;
		path: string;
	};
};

export const uploadSPFDocument = (body: UploadSPFDocumentPayload) => {
	return fetcher<string>({
		api: '/spf/upload-signed-document',
		body,
	});
};

export const useUploadSPFDocument = () => {
	return useMutation({
		mutationFn: uploadSPFDocument,
	});
};

export const downloadSPFDocument = (spfId: number) => {
	return fetcher<{ signedUrl: string }>({
		api: '/spf/download-document',
		body: {
			spfId,
		},
	});
};

export const useDownloadSPFDocumentQuery = () => {
	return useMutation({
		mutationFn: downloadSPFDocument,
	});
};

export const getSPFThingsTodo = () => {
	return fetcher<number>({
		api: '/spf/get-things-to-do',
	});
};

export const useSPFThingsTodo = () => {
	return useQuery({
		queryKey: ['getSPFThingsTodo'],
		queryFn: async () => getSPFThingsTodo(),
	});
};
